import { Credentials, Geotab, User } from "@fairfleet/geotab";
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import LocalStorageNames from "@/constants/LocalStorageNames";
import { findSignedInUser } from "@/utilities/findSignedInUser";
import { createGeotabClient } from "@/utilities/geotab/createGeotabClient";

// Interface for the context type
interface GeotabClientContextType {
  geotabClient: Geotab;
  updateGeotabClient: (newCredentials: Credentials) => void;
  switchSession: (sessionId: string) => void;
  clearGeotabClient: (sessionId: string) => void;
  username: string;
  database: string;
  geotabView: boolean;
  activeSessionId: string | null;
  internalSessionId: string | null;
  sessions: string[]; // List of available sessions
  isLoading: boolean; // Is the client being
  /** The signed-in user of the current client; `null` while loading or when it could not be found. */
  me: User | null;
  /** Whether the lookup of `me` for the current client has settled (found, not found or failed). */
  meResolved: boolean;
}

// Default geotabClient initialization (null while loading)
const defaultGeotabClient = createGeotabClient();

export const GeotabClientContext = createContext<GeotabClientContextType>({
  geotabClient: defaultGeotabClient,
  updateGeotabClient: () => ({}),
  switchSession: () => ({}),
  clearGeotabClient: () => ({}),
  username: "",
  database: "",
  geotabView: false,
  activeSessionId: null,
  internalSessionId: null,
  sessions: [],
  isLoading: true, // Start with loading
  me: null,
  meResolved: false,
});

export const useGeotabClient = () => {
  return useContext(GeotabClientContext);
};

// Utility function to get all available sessions from localStorage
const getStoredSessions = () => {
  const keys = Object.keys(localStorage);
  return keys
    .filter(key => key.startsWith(LocalStorageNames.sessionKeyPrefix))
    .map(key => key.replace(LocalStorageNames.sessionKeyPrefix, ""));
};

// Provider component with session handling
export const GeotabClientProvider: React.FC<{ children: ReactNode; credentials?: Credentials }> = ({
  children,
  credentials,
}) => {
  const [geotabClient, setGeotabClient] = useState<Geotab>(createGeotabClient({ credentials }));
  const [username, setUsername] = useState<string>(credentials?.userName ?? "");
  const [database, setDatabase] = useState<string>(credentials?.database ?? "");
  const [sessions, setSessions] = useState<string[]>(getStoredSessions);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [internalSessionId, setInternalSessionId] = useState<string | null>(
    credentials?.sessionId ?? null,
  );
  const [isLoading, setIsLoading] = useState(true);
  // `me` is tagged with the client and username it was looked up for, so after a session switch
  // the previous session's user is never exposed while the new lookup runs.
  const [meLookup, setMeLookup] = useState<{ client: Geotab; username: string; user: User | null } | null>(
    null,
  );

  useEffect(() => {
    if (isLoading) return;
    let cancelled = false;
    const trimmed = username.trim();
    const settle = (user: User | null) => {
      if (!cancelled) setMeLookup({ client: geotabClient, username, user });
    };
    if (!trimmed) {
      settle(null);
      return;
    }
    geotabClient.get("User", { name: trimmed }, 5).then(
      users => settle(findSignedInUser(users, trimmed)),
      (error: unknown) => {
        console.error("Could not look up the signed-in user", error);
        settle(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [isLoading, geotabClient, username]);

  const meResolved =
    !isLoading && meLookup !== null && meLookup.client === geotabClient && meLookup.username === username;
  const me = meResolved ? meLookup.user : null;

  useEffect(() => {
    const savedSessionId = localStorage.getItem(LocalStorageNames.activeSessionKey);
    if (savedSessionId) {
      setActiveSessionId(savedSessionId);
    } else {
      setIsLoading(false);
    }
  }, []);

  // Load credentials from localStorage if activeSessionId is set
  useEffect(() => {
    if (activeSessionId) {
      const savedCredentials = localStorage.getItem(
        `${LocalStorageNames.sessionKeyPrefix}${activeSessionId}`,
      );
      if (savedCredentials) {
        const parsedCredentials: Credentials = JSON.parse(savedCredentials);
        setGeotabClient(createGeotabClient({ credentials: parsedCredentials }));
        setUsername(parsedCredentials.userName);
        setDatabase(parsedCredentials.database);
        setInternalSessionId(parsedCredentials.sessionId);
        setIsLoading(false); // Client loaded, stop loading
      }
    }
  }, [activeSessionId]);

  // Function to update the Geotab client and store credentials in localStorage
  const updateGeotabClient = useCallback((newCredentials: Credentials) => {
    const sessionId = newCredentials.userName + " (" + newCredentials.database + ")";
    setGeotabClient(createGeotabClient({ credentials: newCredentials }));
    setUsername(newCredentials.userName);
    setInternalSessionId(newCredentials.sessionId);
    setDatabase(newCredentials.database);
    localStorage.setItem(
      `${LocalStorageNames.sessionKeyPrefix}${sessionId}`,
      JSON.stringify(newCredentials),
    );
    localStorage.setItem(LocalStorageNames.activeSessionKey, sessionId); // Persist the active session ID
    setActiveSessionId(sessionId); // Automatically switch to the new session
    setSessions(getStoredSessions());
    setIsLoading(false); // Set loading to false once the client is set
  }, []);

  // Function to switch between sessions
  const switchSession = useCallback(
    (sessionId: string) => {
      const savedCredentials = localStorage.getItem(
        `${LocalStorageNames.sessionKeyPrefix}${sessionId}`,
      );
      if (savedCredentials) {
        const parsedCredentials: Credentials = JSON.parse(savedCredentials);
        setGeotabClient(createGeotabClient({ credentials: parsedCredentials }));
        setUsername(parsedCredentials.userName);
        setInternalSessionId(parsedCredentials.sessionId);
        setDatabase(parsedCredentials.database);
        localStorage.setItem(LocalStorageNames.activeSessionKey, sessionId); // Persist the active session ID
        setActiveSessionId(sessionId);
      } else {
        setGeotabClient(createGeotabClient({ credentials })); // Set to null while switching session
        setUsername("");
        setDatabase("");
        setInternalSessionId(null);
        localStorage.removeItem(LocalStorageNames.activeSessionKey); // Clear active session
        setActiveSessionId(null);
      }
      setIsLoading(false); // Always set loading to false once done switching
    },
    [credentials],
  );

  // Function to clear the Geotab client and remove credentials for a specific session
  const clearGeotabClient = useCallback(
    (sessionId: string) => {
      localStorage.removeItem(`${LocalStorageNames.sessionKeyPrefix}${sessionId}`);
      if (activeSessionId === sessionId) {
        localStorage.removeItem(LocalStorageNames.activeSessionKey);
        setActiveSessionId(null);
      }
      const newSessions = getStoredSessions();
      setSessions(newSessions);
      if (newSessions.length === 0) {
        setGeotabClient(createGeotabClient());
        setUsername("");
        setInternalSessionId(null);
        setDatabase("");
      } else {
        switchSession(newSessions[0]);
      }
      setIsLoading(false);
    },
    [activeSessionId, switchSession],
  );

  return (
    <GeotabClientContext.Provider
      value={{
        geotabClient,
        updateGeotabClient,
        switchSession,
        clearGeotabClient,
        username,
        database,
        geotabView: !!credentials,
        activeSessionId,
        sessions,
        isLoading,
        me,
        meResolved,
        internalSessionId,
      }}
    >
      {children}
    </GeotabClientContext.Provider>
  );
};
